/* config-admin MRI field sets → the cockpit's `MriFieldSetAdmin` / `MriFieldAdmin`.
 *
 * The admin API publishes two global field sets (`full` and `termination`) in
 * place of the per-catalog MRI catalogue that used to live here. Each set is
 * an ordered list of fields the operator fills in when raising a case of that
 * kind; the form sends drafts in, the API returns the superseded/active rows
 * back. This file only maps one shape to the other — the service owns the
 * supersede flow and `If-Match`.
 *
 * `confidenceFloor` is a string on the wire (the service serialises Decimal that
 * way) and is parsed to a number here, because the form compares it as a number
 * and a string compare would mis-order `0.9` above `0.85`. A null floor stays
 * null — the form shows a dash rather than `0.00`, the same bargain as the
 * catalog mapper. */

import type { components } from '@/shared/api/generated/openapi';
import {
  mriDimension,
  mriFieldSetKind,
  mriValueType,
  type MriFieldAdmin,
  type MriFieldDraft,
  type MriFieldSetAdmin,
  type MriFieldSetDraft,
  type MriFieldSetPatch,
} from '@/shared/domain/mriAdmin';

export type WireMriFieldSet = components['schemas']['MriFieldSetModel'];
export type WireMriFieldSetCreate = components['schemas']['MriFieldSetCreate'];
export type WireMriFieldSetPatch = components['schemas']['MriFieldSetPatch'];
export type WireMriField = components['schemas']['MriFieldModel'];
export type WireMriFieldCreate = components['schemas']['MriFieldCreate'];

/** Parse a wire confidenceFloor (`"0.90"`, `0.9`, or null) to a number or null.
    A non-numeric string is treated as null — the service only ever sends a
    decimal string, so the fallback is for safety, not for a real case. */
function toFloor(raw: string | number | null | undefined): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const value = typeof raw === 'number' ? raw : Number(raw);
  return Number.isNaN(value) ? null : value;
}

/** One wire field → the cockpit's `MriFieldAdmin`. */
export function toMriField(raw: WireMriField): MriFieldAdmin {
  return {
    id: raw.id,
    setId: raw.setId,
    name: raw.name,
    businessName: raw.businessName,
    valueType: mriValueType(raw.valueType),
    minCardinality: raw.minCardinality,
    maxCardinality: raw.maxCardinality,
    mriDimension: mriDimension(raw.mriDimension),
    confidenceFloor: toFloor(raw.confidenceFloor),
    conditionalRuleKey: raw.conditionalRuleKey,
    validationRuleKey: raw.validationRuleKey,
    displayOrder: raw.displayOrder,
    validForStart: raw.validForStart,
    validForEnd: raw.validForEnd,
  };
}

/** One wire set (with its fields) → the cockpit's `MriFieldSetAdmin`. */
export function toMriFieldSet(raw: WireMriFieldSet): MriFieldSetAdmin {
  return {
    id: raw.id,
    setKind: mriFieldSetKind(raw.setKind),
    name: raw.name,
    status: raw.status,
    versionNo: raw.versionNo,
    validForStart: raw.validForStart,
    validForEnd: raw.validForEnd,
    fields: raw.fields.map(toMriField),
  };
}

/** A field draft → the wire create body. Numbers go as numbers; the optional
    fields are omitted when absent so the service applies its own defaults
    rather than receiving an explicit null. */
export function toWireMriFieldCreate(draft: MriFieldDraft): WireMriFieldCreate {
  return {
    name: draft.name,
    ...(draft.businessName === undefined ? {} : { businessName: draft.businessName }),
    valueType: draft.valueType,
    minCardinality: draft.minCardinality,
    maxCardinality: draft.maxCardinality ?? null,
    mriDimension: draft.mriDimension,
    ...(draft.confidenceFloor === undefined ? {} : { confidenceFloor: draft.confidenceFloor }),
    ...(draft.conditionalRuleKey === undefined
      ? {}
      : { conditionalRuleKey: draft.conditionalRuleKey }),
    ...(draft.validationRuleKey === undefined
      ? {}
      : { validationRuleKey: draft.validationRuleKey }),
    ...(draft.displayOrder === undefined ? {} : { displayOrder: draft.displayOrder }),
  };
}

/** A set draft → the wire create/replace body. */
export function toWireMriFieldSetCreate(draft: MriFieldSetDraft): WireMriFieldSetCreate {
  return {
    setKind: draft.setKind,
    name: draft.name,
    fields: draft.fields.map(toWireMriFieldCreate),
  };
}

/** A set patch draft → the wire patch body. */
export function toWireMriFieldSetPatch(patch: MriFieldSetPatch): WireMriFieldSetPatch {
  return patch.name === undefined ? {} : { name: patch.name };
}
